import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Receipt, FileText, Plus, X, Pencil, Anchor } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { VatBreakdown, ShowTaskButton } from "./maintenance/components";
import FileUploader from "../../components/FileUploader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { InvoiceMessages, InvoiceHistoryList } from "../../components/InvoiceThread";
import { SupplierStatement } from "../../components/SupplierStatement";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { labelForSupplierCategory } from "../../lib/supplierCategories";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const STATUS_STYLES = {
  task_confirmed: "bg-sky-50 text-sky-700 hover:bg-sky-50",
  paid: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50",
  payment_confirmed: "bg-emerald-600 text-white hover:bg-emerald-600",
  waiting: "bg-amber-50 text-amber-700 hover:bg-amber-50",
  dispute: "bg-red-50 text-red-700 hover:bg-red-50",
};
const STATUS_LABEL = {
  task_confirmed: "Task Confirmed",
  paid: "Payment done",
  payment_confirmed: "Payment confirmed",
  waiting: "Awaiting payment",
  dispute: "In dispute",
};

function fmtAmount(currency, amount) {
  return `${currency || ""} ${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
}

function isOverdue(inv) {
  const due = (inv.due_date || "").slice(0, 10);
  return inv.status === "waiting" && due && due < new Date().toISOString().slice(0, 10);
}

export function lastStatusAt(inv) {
  const h = (inv.status_history || []).filter((e) => e.to);
  const last = h[h.length - 1];
  return ((last && last.at) || inv.updated_at || inv.created_at || "").slice(0, 10);
}

const CARD_TONE = {
  waiting: ["border-amber-200", "bg-amber-50/70 border-amber-100"],
  task_confirmed: ["border-sky-200", "bg-sky-50/70 border-sky-100"],
  paid: ["border-emerald-200", "bg-emerald-50/70 border-emerald-100"],
  payment_confirmed: ["border-emerald-200", "bg-emerald-50/70 border-emerald-100"],
  dispute: ["border-red-200", "bg-red-50/70 border-red-100"],
};

function InvoiceRow({ inv, onOpen }) {
  const overdue = isOverdue(inv);
  const [borderCls, headerCls] = overdue
    ? ["border-red-300 ring-1 ring-red-200", "bg-red-50 border-red-100"]
    : CARD_TONE[inv.status] || ["border-zinc-200", "bg-zinc-50 border-zinc-100"];
  return (
    <div
      className={`rounded-xl border overflow-hidden cursor-pointer transition-shadow hover:shadow-md ${borderCls}`}
      data-testid={`sup-invoice-${inv.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(inv)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(inv); } }}
    >
      <div className={`flex items-start gap-2 px-3 py-2 border-b ${headerCls}`}>
        <div className="min-w-0 flex-1">
          {inv.yacht_name && (
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-sky-700 truncate">
              <Anchor className="h-3 w-3 shrink-0" />{inv.yacht_name}
            </div>
          )}
          <div className="font-semibold text-sm flex items-center gap-1.5 text-zinc-900">
            Invoice {inv.invoice_number || "(no number)"}
            {inv.unread > 0 && <span className="rounded-full bg-red-500 text-white text-[10px] px-1.5 leading-4" data-testid={`sup-invoice-unread-${inv.id}`}>{inv.unread}</span>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-sm font-semibold">{fmtAmount(inv.currency, inv.amount)}</div>
          <Badge className={`mt-0.5 ${STATUS_STYLES[inv.status] || "bg-zinc-100 text-zinc-600 hover:bg-zinc-100"}`} data-testid={`sup-invoice-status-${inv.id}`}>
            {STATUS_LABEL[inv.status] || inv.status}
          </Badge>
        </div>
      </div>
      <div className="px-3 py-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        {inv.task_title && <span className="text-[11px] font-medium text-zinc-700 truncate max-w-full">{inv.task_title}</span>}
        <span className="flex flex-wrap items-center gap-x-2 text-[11px] text-zinc-500">
          {inv.issue_date && <span>issued {inv.issue_date.slice(0, 10)}</span>}
          {inv.due_date && (
            <span className={overdue ? "text-red-600 font-semibold" : ""}>
              · due {inv.due_date.slice(0, 10)}{overdue ? " — OVERDUE" : ""}
            </span>
          )}
          {inv.status === "paid" && inv.paid_date && <span>· paid {inv.paid_date.slice(0, 10)}</span>}
          <span className="text-[10px] text-zinc-400" data-testid={`sup-invoice-status-date-${inv.id}`}>· {lastStatusAt(inv)}</span>
        </span>
        {inv.linked_maintenance_id && (
          <span className="ml-auto">
            <ShowTaskButton maintId={inv.linked_maintenance_id} supplier testid={`sup-show-task-btn-${inv.id}`} />
          </span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, tone }) {
  const toneCls = tone === "red" ? "border-red-200 bg-red-50" : "border-zinc-200 bg-white";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`} data-testid={testid}>
      <div className={`text-xs uppercase tracking-wider ${tone === "red" ? "text-red-500" : "text-zinc-400"}`}>{label}</div>
      <div className={`font-display text-2xl mt-1 ${tone === "red" ? "text-red-700" : ""}`}>{value}</div>
    </div>
  );
}

function MiniLabel({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{children}</div>;
}

/* Editable final-invoice items (text + optional price per line) with a VAT %
   field. The total is derived from the priced lines + VAT — never hand-typed. */
function ScopeItemsEditor({ items, setItems, currency, vat, setVat }) {
  const priced = items.filter((i) => i.price !== "" && i.price != null);
  const subtotal = priced.reduce((s, i) => s + Number(i.price || 0), 0);
  const vatPct = vat === "" || vat == null ? null : Number(vat);
  const vatAmt = vatPct ? Math.round(subtotal * vatPct) / 100 : 0;
  const total = Math.round((subtotal + vatAmt) * 100) / 100;
  const upd = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-2" data-testid="sup-inv-items-editor">
      <MiniLabel>Final invoice items</MiniLabel>
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="text-xs text-zinc-400 w-4 shrink-0 pt-2">{i + 1}.</span>
          <Textarea value={it.text} onChange={(e) => upd(i, { text: e.target.value })} rows={3} placeholder="Item description" className="flex-1 text-sm min-h-0 bg-white" data-testid={`sup-inv-item-text-${i}`} />
          <Input type="number" step="0.01" min="0" value={it.price} onChange={(e) => upd(i, { price: e.target.value })} placeholder="Price" className="w-20 h-8 text-sm bg-white no-spinner shrink-0" data-testid={`sup-inv-item-price-${i}`} />
          <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-red-600 shrink-0 pt-2" data-testid={`sup-inv-item-remove-${i}`} title="Remove item">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <div className="flex items-start gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { text: "", price: "" }])} data-testid="sup-inv-item-add">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add item
        </Button>
        <div className="flex items-center gap-1 ml-auto">
          <Input type="number" min="0" max="100" placeholder="VAT" value={vat} onChange={(e) => setVat(e.target.value)} className="w-16 h-8 text-sm bg-white no-spinner" data-testid="sup-inv-vat" />
          <span className="text-xs text-zinc-500">VAT&nbsp;%</span>
        </div>
      </div>
      {priced.length > 0 && (
        <div className="border-t border-zinc-200 pt-2 space-y-0.5 text-xs" data-testid="sup-inv-computed-total">
          {vatPct != null && (
            <>
              <div className="flex justify-between text-zinc-500"><span>Subtotal</span><span>{fmtAmount(currency, subtotal)}</span></div>
              <div className="flex justify-between text-zinc-500"><span>VAT {vatPct}%</span><span>{fmtAmount(currency, vatAmt)}</span></div>
            </>
          )}
          <div className="flex justify-between text-sm font-semibold">
            <span>Total{vatPct != null ? " incl. VAT" : ""} (recalculated)</span><span>{fmtAmount(currency, total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* Block 2 of the supplier detail — scope, total, edit-final-invoice, issue
   date, due date, invoice number, save/send, attach PDF (spec order). */
function SupplierInvoiceEditor({ inv, onUpdated, onSaved, onSend, sending }) {
  const [form, setForm] = useState({
    invoice_number: inv.invoice_number || "",
    due_date: (inv.due_date || "").slice(0, 10),
    file_path: inv.file_path || null,
    vat: inv.vat_percent != null ? String(inv.vat_percent) : "",
  });
  const [items, setItems] = useState(null); // null → not editing scope
  const [saving, setSaving] = useState(false);

  const readOnly = ["paid", "payment_confirmed"].includes(inv.status);
  // A disputed invoice must be corrected + saved before it can be re-sent:
  // the Send button only appears once an 'edited' history entry is newer
  // than the last dispute (the post-save "send now?" prompt covers the rest).
  const hist = inv.status_history || [];
  const lastDisputeAt = hist.filter((h) => h.to === "dispute").map((h) => h.at || "").sort().pop();
  const editedSinceDispute = !lastDisputeAt || hist.some((h) => h.action === "edited" && (h.at || "") > lastDisputeAt);
  const canSend = inv.status === "task_confirmed" || (inv.status === "dispute" && editedSinceDispute);
  // On a disputed invoice there's nothing to save until something changed —
  // only the (red-highlighted) "Edit final invoice" button shows initially.
  const dirty = items !== null
    || form.invoice_number !== (inv.invoice_number || "")
    || form.due_date !== (inv.due_date || "").slice(0, 10)
    || form.vat !== (inv.vat_percent != null ? String(inv.vat_percent) : "")
    || (form.file_path || null) !== (inv.file_path || null);
  const showSave = inv.status !== "dispute" || dirty;
  const vatPct = form.vat === "" ? null : Number(form.vat);
  const priced = (items || []).filter((i) => i.price !== "" && i.price != null);
  const liveSubtotal = priced.reduce((s, i) => s + Number(i.price || 0), 0);
  const liveTotal = items && priced.length > 0
    ? Math.round(liveSubtotal * (1 + (vatPct || 0) / 100) * 100) / 100
    : null;
  const savedPrices = (inv.line_items || []).filter((li) => li.price != null);
  const displayTotal = liveTotal != null
    ? liveTotal
    : (inv.amount ?? (savedPrices.length > 0 ? savedPrices.reduce((s, li) => s + Number(li.price || 0), 0) : 0));

  const startEditItems = () => setItems(
    (inv.line_items || []).map((li) => ({ text: li.text || "", price: li.price ?? "" })),
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        amount: liveTotal,
        vat_percent: vatPct,
        invoice_number: form.invoice_number || null,
        due_date: form.due_date || null,
        file_path: form.file_path || null,
        line_items: items
          ? items.filter((i) => (i.text || "").trim()).map((i) => ({
              text: i.text.trim(),
              price: i.price === "" || i.price == null ? null : Number(i.price),
            }))
          : null,
      };
      const { data } = await api.patch(`/invoices/${inv.id}/supplier-edit`, payload);
      toast.success("Invoice saved");
      onSaved?.(data);
      onUpdated?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-3 border-t border-zinc-200 pt-3 space-y-3" data-testid="sup-invoice-editor">
      {/* 7. Scope */}
      {items === null && (inv.line_items || []).length > 0 && (
        <div data-testid="sup-inv-scope">
          <MiniLabel>Scope</MiniLabel>
          <ul className="space-y-0.5 mt-1">
            {inv.line_items.map((s, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-sm text-zinc-800">
                <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]"><span className="text-zinc-400 font-medium mr-1">{i + 1}.</span>{s.text}</span>
                {s.price != null && <span className="text-zinc-500 text-xs whitespace-nowrap shrink-0">{fmtAmount(inv.currency, s.price)}</span>}
              </li>
            ))}
          </ul>
          <VatBreakdown items={inv.line_items} amount={inv.amount} currency={inv.currency} vatPercent={inv.vat_percent} testid="sup-inv-vat-view" />
        </div>
      )}
      {items !== null && <ScopeItemsEditor items={items} setItems={setItems} currency={inv.currency} vat={form.vat} setVat={(v) => setForm((f) => ({ ...f, vat: v }))} />}

      {/* 8-9. Total — read-only, derived from the scope (+ VAT) */}
      <div className="flex items-center justify-between rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid="sup-inv-total">
        <span className="text-sm font-medium text-zinc-700">Total{(items !== null ? vatPct != null : inv.vat_percent != null) ? " incl. VAT" : ""}</span>
        <span className="text-sm font-semibold font-mono">{fmtAmount(inv.currency, displayTotal)}</span>
      </div>
      <p className="text-[11px] text-zinc-500 -mt-2">The total is calculated from the scope details{(items !== null ? vatPct != null : inv.vat_percent != null) ? " plus VAT" : ""} and can't be edited directly.</p>

      {inv.prepay_deduction > 0 && (
        <div className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900" data-testid="sup-inv-prepay">
          <div className="flex items-baseline justify-between">
            <span>Prepayment received</span>
            <span className="font-mono">− {fmtAmount(inv.currency, inv.prepay_deduction)}</span>
          </div>
          <div className="flex items-baseline justify-between font-semibold mt-0.5 pt-0.5 border-t border-teal-200">
            <span>Balance due</span>
            <span className="font-mono" data-testid="sup-inv-balance-due">{fmtAmount(inv.currency, Number(items !== null ? displayTotal : inv.amount) - Number(inv.prepay_deduction))}</span>
          </div>
        </div>
      )}

      {/* 10. Edit final invoice — highlighted red while in dispute */}
      {!readOnly && items === null && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={startEditItems}
          data-testid="sup-inv-edit-final"
          className={inv.status === "dispute" ? "border-red-300 text-red-700 hover:bg-red-50" : undefined}
        >
          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit final invoice
        </Button>
      )}

      {/* 11-13. Issue date (read-only) · Due date · Invoice number */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <MiniLabel>Issue date</MiniLabel>
          <div className="h-9 flex items-center px-3 text-sm rounded-md border border-zinc-200 bg-zinc-50 text-zinc-700" data-testid="sup-inv-issue-date" title="Set automatically when the invoice is first sent to the Yacht">
            {inv.issue_date ? inv.issue_date.slice(0, 10) : "set on first send"}
          </div>
        </div>
        <div>
          <MiniLabel>Due date</MiniLabel>
          {readOnly ? (
            <div className="h-9 flex items-center px-3 text-sm rounded-md border border-zinc-200 bg-zinc-50 text-zinc-700">{(inv.due_date || "").slice(0, 10) || "—"}</div>
          ) : (
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="sup-inv-due" />
          )}
        </div>
        <div className="col-span-2">
          <MiniLabel>Invoice # *</MiniLabel>
          {readOnly ? (
            <div className="h-9 flex items-center px-3 text-sm rounded-md border border-zinc-200 bg-zinc-50 text-zinc-700">{inv.invoice_number || "—"}</div>
          ) : (
            <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="sup-inv-number" />
          )}
        </div>
      </div>

      {/* 14. Save + Send side by side (Save hidden on an untouched dispute) */}
      {!readOnly && (
        <div className="flex items-center gap-2">
          {showSave && (
            <Button onClick={save} disabled={saving} className="bg-ink hover:bg-black" data-testid="sup-inv-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save invoice"}
            </Button>
          )}
          {canSend && (
            <Button variant="outline" onClick={onSend} disabled={sending || saving} data-testid="sup-inv-send" className="border-sky-300 text-sky-700 hover:bg-sky-50">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (inv.status === "dispute" ? "Send corrected invoice" : "Send invoice")}
            </Button>
          )}
        </div>
      )}

      {/* 16. Attach PDF */}
      <div className="flex items-center gap-3 flex-wrap">
        {!readOnly && (
          <FileUploader
            yacht_id={inv.yacht_id}
            folder="invoices"
            accept="application/pdf,image/*"
            label={form.file_path ? "Replace PDF" : "Attach invoice PDF"}
            testid="sup-inv-upload"
            onUploaded={(path) => { setForm((f) => ({ ...f, file_path: path })); toast.success("File attached — remember to Save"); }}
          />
        )}
        {(form.file_path || inv.file_path) && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${form.file_path || inv.file_path}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-sky-700 border border-sky-200 bg-sky-50 rounded px-2 py-0.5 hover:bg-sky-100"
            data-testid="sup-invoice-file"
          >
            <FileText className="h-3 w-3" /> View invoice document
          </a>
        )}
      </div>
    </div>
  );
}

function InvoiceDetail({ inv, onClose, onUpdated }) {
  // "Send to Yacht now?" prompt lives here so it survives editor remounts.
  const [askSend, setAskSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { user } = useAuth();
  const canInvoice = user?.supplier_permissions?.can_invoice !== false;
  if (!inv) return null;
  const confirmedAt = inv.task_confirmed_at || (inv.linked_maintenance_id ? inv.created_at : null);
  const handleSaved = (data) => {
    if (data.status === "task_confirmed" || data.status === "dispute") setAskSend(true);
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/supplier-status`, { status: "waiting" });
      toast.success("Invoice sent to the Yacht — awaiting payment");
      setAskSend(false);
      onUpdated?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSending(false); }
  };
  const confirmPayment = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/supplier-status`, { status: "payment_confirmed" });
      toast.success("Payment confirmed");
      onUpdated?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setConfirming(false); }
  };
  return (
    <Dialog open={!!inv} onOpenChange={(o) => { if (!o) { setAskSend(false); onClose(); } }}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 p-4 sm:p-6" data-testid="sup-invoice-detail">
        {/* ── Block 1: yacht · invoice#+status · task · category · confirmed · description ── */}
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg pr-6" data-testid="sup-inv-yacht-name">{inv.yacht_name || "Yacht"}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center gap-2 flex-wrap" data-testid="sup-inv-number-row">
              <span className="text-sm text-zinc-700">Invoice {inv.invoice_number || "(no number)"}</span>
              <Badge className={STATUS_STYLES[inv.status] || "bg-zinc-100 text-zinc-600"}>{STATUS_LABEL[inv.status] || inv.status}</Badge>
              <span className="text-[10px] text-zinc-400" data-testid="sup-inv-status-date">since {lastStatusAt(inv)}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        {inv.linked_maintenance_id && (
          <div className="-mt-1">
            <ShowTaskButton maintId={inv.linked_maintenance_id} supplier testid="sup-inv-show-task" />
          </div>
        )}

        {inv.task_title && <div className="text-sm font-medium text-zinc-900" data-testid="sup-inv-task-title">{inv.task_title}</div>}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {inv.category && (
            <div><MiniLabel>Category</MiniLabel><div>{labelForSupplierCategory(inv.category)}</div></div>
          )}
          {confirmedAt && (
            <div data-testid="sup-inv-confirmed-at"><MiniLabel>Task confirmed</MiniLabel><div>{confirmedAt.slice(0, 10)}</div></div>
          )}
        </div>
        {inv.task_description && (
          <div data-testid="sup-inv-description">
            <MiniLabel>Description</MiniLabel>
            <div className="text-sm text-zinc-700 whitespace-pre-wrap">{inv.task_description}</div>
          </div>
        )}

        {/* ── Block 2: scope · total · edit · issue/due/number · save+send · pdf ── */}
        {canInvoice ? (
          <SupplierInvoiceEditor
            key={inv.updated_at || inv.id}
            inv={inv}
            onUpdated={onUpdated}
            onSaved={handleSaved}
            onSend={sendNow}
            sending={sending}
          />
        ) : (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800" data-testid="sup-inv-no-permission">
            Your company admin hasn't allowed you to manage invoices — you can view and message only.
          </div>
        )}

        {canInvoice && inv.status === "paid" && (
          <div className="mt-1 rounded-md border border-sky-200 bg-sky-50 p-3">
            <p className="text-xs text-sky-800 mb-2">The owner marked this invoice as paid. Confirm once the money arrived.</p>
            <Button size="sm" onClick={confirmPayment} disabled={confirming} className="bg-ink hover:bg-black" data-testid="sup-inv-confirm-payment">
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm payment received"}
            </Button>
          </div>
        )}
        {inv.payment_confirmation_path && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${inv.payment_confirmation_path}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-2 py-1 hover:bg-emerald-100 w-fit"
            data-testid="sup-inv-payment-confirmation"
          >
            <FileText className="h-3 w-3" /> View the Yacht's payment confirmation
          </a>
        )}
        {inv.status === "dispute" && (
          <div className="mt-1 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="sup-inv-dispute-note">
            The Yacht disputed this invoice — see Messages below for the reason. Correct the invoice, save, then send the corrected version. The issue date stays as is; you may set a new due date.
          </div>
        )}
        {inv.status === "payment_confirmed" && (
          <p className="text-xs text-zinc-500">Payment confirmed — this invoice is closed.</p>
        )}

        {/* ── Block 3: messages ── */}
        <div className="mt-3 border-t border-zinc-200 pt-3">
          <MiniLabel>Messages</MiniLabel>
          <div className="mt-2">
            <InvoiceMessages invoiceId={inv.id} initialMessages={inv.messages || []} mySide="supplier" onUpdated={onUpdated} />
          </div>
        </div>

        {/* ── Block 4: history ── */}
        <div className="mt-3 border-t border-zinc-200 pt-3" data-testid="sup-inv-history">
          <MiniLabel>History</MiniLabel>
          <div className="mt-2">
            <InvoiceHistoryList history={inv.status_history || []} />
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} data-testid="sup-invoice-detail-close">Close</Button>
        </DialogFooter>

        {/* 15. Post-save prompt: "send it to the Yacht now?" */}
        <Dialog open={askSend} onOpenChange={(o) => { if (!o) setAskSend(false); }}>
          <DialogContent className="max-w-sm" data-testid="sup-inv-send-dialog">
            <DialogHeader>
              <DialogTitle>{inv.status === "dispute" ? "Send the corrected invoice to the Yacht now?" : "Do you want to send this invoice to the Yacht now?"}</DialogTitle>
              <DialogDescription>
                Yes — the status changes to <strong>Awaiting payment</strong> and the Yacht is notified.
                No — the invoice stays saved; you can send it later.
                {!inv.invoice_number && <span className="block mt-1 text-red-600">An invoice number is required before sending.</span>}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setAskSend(false)} data-testid="sup-inv-send-no">No, just save</Button>
              <Button onClick={sendNow} disabled={sending || !inv.invoice_number} className="bg-ink hover:bg-black" data-testid="sup-inv-send-yes">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, send now"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default function SupplierInvoices() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/supplier/invoices");
      setData(data);
      // ?open={invoiceId} deep-link (from a task's "Show invoice" button).
      const openId = searchParams.get("open");
      if (openId) {
        const row = (data.invoices || []).find((r) => r.id === openId);
        if (row) setDetail(row);
        const next = new URLSearchParams(searchParams);
        next.delete("open");
        setSearchParams(next, { replace: true });
      }
    }
    catch (e) { toast.error(fmtErr(e)); }
  }, [searchParams, setSearchParams]);
  useEffect(() => { load(); }, [load]);

  if (data === null) {
    return <div className="py-10 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  }
  const { invoices = [], summary = {} } = data;
  const overdueList = invoices.filter(isOverdue);
  const overdueSum = overdueList.reduce((s, i) => s + (i.amount || 0), 0);
  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="supplier-invoices-page">
      <h1 className="font-display text-2xl mb-4 flex items-center gap-2"><Receipt className="h-5 w-5 text-zinc-400" /> Invoices</h1>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Outstanding" value={fmtAmount("", summary.outstanding)} testid="sup-inv-outstanding" />
        <Stat
          label={`Overdue${overdueList.length ? ` (${overdueList.length})` : ""}`}
          value={fmtAmount("", overdueSum)}
          testid="sup-inv-overdue"
          tone={overdueList.length ? "red" : undefined}
        />
        <Stat label="Paid" value={fmtAmount("", summary.paid)} testid="sup-inv-paid" />
        <Stat label="Invoices" value={summary.count ?? 0} testid="sup-inv-count" />
      </div>
      <Tabs defaultValue="invoices">
        <TabsList className="mb-3">
          <TabsTrigger value="invoices" data-testid="sup-inv-tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="statement" data-testid="sup-inv-tab-statement">Monthly Statement</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices">
          {invoices.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-300 p-10 text-center text-zinc-500" data-testid="supplier-invoices-empty">
              No invoices recorded against your company yet. When a yacht logs an invoice from you, it will appear here.
            </div>
          ) : (
            <div className="space-y-2" data-testid="supplier-invoices-list">
              {invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} onOpen={setDetail} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="statement">
          <SupplierStatement invoices={invoices} onOpen={setDetail} />
        </TabsContent>
      </Tabs>
      <InvoiceDetail
        inv={detail}
        onClose={() => setDetail(null)}
        onUpdated={(fresh) => {
          // Merge: PATCH/POST responses return the raw doc without the
          // list-enriched fields (yacht_name, unread) — keep them.
          setDetail((prev) => (prev ? { ...prev, ...fresh } : fresh));
          load();
        }}
      />
    </div>
  );
}
